import React, { Component } from "react";
import PropTypes from "prop-types";
import { withHtmlCsp, agentNavGuard } from "../../../../common/agentHtmlGuard";
import ScaledCanvasFrame from "../../../../common/ScaledCanvasFrame";
import MarkCreator from "../../rich_plugins/mark_creator/MarkCreator";
import interact from "interactjs";
import PluginPlaceholder from "../plugin_placeholder/PluginPlaceholder";
import { EDIT_PLUGIN_TEXT } from "../../../../common/actions";
import {
  releaseClick,
  findBox,
  safeDecodeURI,
  normalizeFontSizes,
  stripParagraphTags,
} from "../../../../common/common_tools";

import ExerciseAutoFit from "./ExerciseAutoFit";
import { isUnitlessNumber } from "../../../../common/cssNonUnitProps";
import IdealStudio from "../../../../core/editor/main";
import { LuMove } from "react-icons/lu";
import {
  isSortableBox,
  isSortableContainer,
  isAncestorOrSibling,
  isContainedView,
  isBox,
  isForeignOrigin,
} from "../../../../common/utils";
import "./_editorBox.scss";
import { ID_PREFIX_SORTABLE_CONTAINER } from "../../../../common/constants";
import CKEDitorComponent from "./CKEDitorComponent";
import InlineHtmlEditor from "./InlineHtmlEditor";
import { computeRotatedResize } from "./rotatedResize";
import { splitSlots } from "../../../../common/dnd_slots";
import BoxContextMenu from "./BoxContextMenu";
import AideaSmartElementsOverlay from "./AideaSmartElementsOverlay";
import UnknownPluginBox, { getPluginSafe } from "./UnknownPluginBox";
import {
  buildSnapTargets,
  resolveSnap,
  snapOnRelease,
  snapResizeOnRelease,
  setDragGuides,
  clearDragGuides,
} from "../../../../common/alignment_utils";
import DOMPurify from "dompurify";
import { normalizeRotation } from "../../../utils/rotation";
import { boxZIndex } from "./boxZIndex";

// P10 fix: course-authored links may legitimately set target="_blank" (see the
// same config on the Visor twin, VisorBox.jsx). DOMPurify's default profile
// strips `target`; re-allowed only at the course-text render sink below via
// SAFE_LINK_CONFIG, paired with a rel hook so a restored target can't be used
// for reverse tabnabbing.
const SAFE_LINK_CONFIG = { ADD_ATTR: ["target"] };
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const SNAP_DRAG = 5;
const SNAP_SIZE = 2;
let html2json = require("html2json").html2json;

// Stacking value for the selected box (see `isSelected` in render). Boxes carry
// no z-index of their own — they stack by DOM order — so any positive value
// lifts the selection above every sibling, while staying far below the editor
// chrome (box icons / overlays at 9999).
const SELECTION_LIFT_Z = 50;

// Single selection, or a member of the multi-selection — the same test the
// `selectedBox` class uses.
function isBoxSelected(id, boxSelected, boxSelectedAll) {
  return (Array.isArray(boxSelectedAll) && boxSelectedAll.includes(id)) || id === boxSelected;
}

// The z-index a box rests at once no drag is in flight: the selection lift for
// a selected box, plain DOM order for the rest. Drag handlers write this back
// on drop — they zero the z-index imperatively while dragging, and React would
// not restore its own value afterwards because, as far as it knows, nothing
// changed.
function restingZIndex(id, boxSelected, boxSelectedAll) {
  const z = boxZIndex(id, isBoxSelected(id, boxSelected, boxSelectedAll));
  return z === undefined ? "0" : String(z);
}

/**
 * IdealStudio Box component.
 * @desc It is the main and more complex component by far. It is the one in charge of painting a plugin's template and therefore it has many parts conditioned to the type of plugin.
 */
// The box-anchored pills (#topPillRoot: the element toolbar, the assessment
// menu, the text/image action pill) are positioned once, from the box's rect.
// interact.js drags/resizes the box by transform without a React re-render, so
// during the gesture they hang over the spot the box LEFT — covering the very
// content you are aligning against. Hide the whole pill layer for the gesture;
// it is repositioned and faded back in on drop.
function setBoxPillsVisible(visible) {
  const root = document.getElementById("topPillRoot");
  if (!root) return;
  root.style.transition = "opacity 0.12s ease";
  root.style.opacity = visible ? "" : "0";
  root.style.visibility = visible ? "" : "hidden";
}

export default class EditorBox extends Component {
  /**
   * Constructor
   * @param props React component props
   */
  constructor(props) {
    super(props);
    /**
     * Box border size
     * @type {number}
     */
    this.borderSize = 2;
    this.nodeRef = React.createRef();
    this.contentRef = React.createRef();
    this.blurTextarea = this.blurTextarea.bind(this);
    this.rotate = this.rotate.bind(this);
    this.handleContextMenu = this.handleContextMenu.bind(this);
    this.closeContextMenu = this.closeContextMenu.bind(this);
    // 1-click lightweight inline editing (assessment question/answer text).
    // Formatting lives in the BasicText pill; closing without an actual edit
    // is a pure no-op (InlineHtmlEditor calls onCancel, not onCommit).
    this.state = { inlineEditing: false, contextMenu: null };
  }

  // Commit a lightweight inline edit: store the HTML in the box's __text (same
  // encoded form CKEditor commits) and leave edit mode.
  commitInlineEdit(html) {
    const encoded = encodeURI(html);
    this.props.onTextEditorToggled(this.props.id, false, encoded, html2json(encoded));
    if (this.state.inlineEditing) this.setState({ inlineEditing: false });
  }

  cancelInlineEdit() {
    if (this.state.inlineEditing) this.setState({ inlineEditing: false });
  }

  handleContextMenu(e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ contextMenu: { x: e.clientX, y: e.clientY } });
  }

  closeContextMenu() {
    this.setState({ contextMenu: null });
  }

  /**
   * Renders React Component
   * @returns {code} React rendered component
   */

  handleRotateStart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const toolbar = this.props.pluginToolbars[this.props.id];
    const initialRotation = toolbar.structure.rotation || 0;

    const rect = this.nodeRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

    const onMouseMove = (ev) => {
      const angle = Math.atan2(ev.clientY - centerY, ev.clientX - centerX) * (180 / Math.PI);
      const newRotation = normalizeRotation(initialRotation + angle - startAngle);
      this.props.onBoxRotate(this.props.id, Math.round(newRotation));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  fibOnChangeHandler(event) {
    // Correct answers are stored on the FillInBlank (evaluation) box's exercise,
    // whose id is carried on the exercise object itself. The old fallback picked
    // the first underscore-free box id in the whole scene — wrong whenever more
    // than one box exists.
    let id =
      (this.props.exercises && this.props.exercises.id) ||
      Object.keys(this.props.boxes).find((e) => !e.includes("_"));
    let page = this.props.page;
    let value = event.target.value;
    let index = event.target.id.replace("fib-", "");
    this.props.setCorrectAnswerForFib(id, value, page, index);
  }

  render() {
    let cornerSize = 15;
    let box = this.props.boxes[this.props.id];
    let toolbar = this.props.pluginToolbars[this.props.id];

    if (!box || !toolbar) return null;
    if (!getPluginSafe(toolbar.pluginId)) {
      return <UnknownPluginBox id={this.props.id} box={box} toolbar={toolbar} />;
    }
    let vis = this.props.boxSelected === this.props.id;
    let style = {
      visibility: toolbar.showTextEditor ? "hidden" : "visible",
      overflow: "hidden",
    };

    let textareaStyle = {
      height: toolbar.showTextEditor ? "100%" : "100%",
      display: toolbar.showTextEditor ? "block" : "none",
    };
    let attrs = {};
    let width = toolbar.structure.width;
    let height = toolbar.structure.height;
    let widthUnit = toolbar.structure.widthUnit;
    let heightUnit = toolbar.structure.heightUnit;
    let classNames = "";
    let apiPlugin = IdealStudio.Plugins.get(toolbar.pluginId);
    let config = apiPlugin.getConfig();
    if (config.needsTextEdition) {
      // Standalone text defaults to left. Inside a sortable container
      // (assessment question / answer cells) it INHERITS instead, so the
      // Settings → Structure "Alignment" control — which writes text-align onto
      // the container — actually aligns the text. The old hard-coded "left"
      // overrode that inherited value, so the control did nothing. Per-box
      // alignment (toolbar.state.textAlign) still wins via the boxTextAlign-*
      // class / textFormatStyle below.
      const baseAlign = isSortableContainer(box.container) ? "inherit" : "left";
      textareaStyle.textAlign = baseAlign;
      style.textAlign = baseAlign;
    }
    let container = box.parent;
    // let controls = apiPlugin.getToolbar();
    let marks = {};
    Object.keys(this.props.marks || {}).forEach((mark) => {
      if (this.props.marks[mark].origin === this.props.id) {
        marks[mark] = this.props.marks[mark];
      }
    });

    let toolbarStyle = {};
    for (let propCSS in toolbar.style) {
      if (!isNaN(toolbar.style[propCSS]) && isUnitlessNumber.indexOf(propCSS) === -1) {
        toolbarStyle[propCSS] = toolbar.style[propCSS] / 7 + "em";
      } else {
        toolbarStyle[propCSS] = toolbar.style[propCSS];
      }
    }
    style = { ...style, ...toolbarStyle };
    // Timeline scrubs reset the inner .boxStyle's inline opacity
    // (resetElementAnimationState); it restores this authored value instead of
    // wiping it, which made Settings → Opacity look like it did nothing.
    if (style.opacity !== undefined && style.opacity !== null && style.opacity !== "") {
      attrs["data-base-opacity"] = String(style.opacity);
    }
    if (toolbar.structure.height === "auto" && config.needsTextEdition) {
      style.height = "auto";
    }

    Object.assign(textareaStyle, style);
    textareaStyle.visibility = "visible";
    // Mirror the BasicText pill's box-level formatting (font family/size/
    // color/highlight/bold/italic/underline/align, applied to the view-mode
    // wrapper below) onto the edit-mode contentEditable div too. Without
    // this, changing font while selected-but-not-editing only styled the
    // (now-hidden) view wrapper — entering edit mode showed CKEditor's own
    // unstyled div instead, which looked like the font "reverting".
    if (toolbar.state.fontScale) {
      textareaStyle.fontSize = Math.round(toolbar.state.fontScale * 100) + "%";
    }
    if (toolbar.state.textAlign) {
      textareaStyle.textAlign = toolbar.state.textAlign;
    }
    if (toolbar.state.textFontFamily) {
      textareaStyle.fontFamily = toolbar.state.textFontFamily;
    }
    if (toolbar.state.textFontSize && !toolbar.showTextEditor) {
      // ponytail: ALL text boxes scale with the canvas em base (changeFontBase
      // sets font-size in em on the canvas root). Standalone boxes used to
      // keep literal px, but CKEditor renders sizes in em — the same value
      // displayed differently in the two tools whenever the canvas base ≠ 14
      // (the "grows on close / shrinks on open" bug), so px is gone.
      // While the full CKEditor is OPEN the mirror is skipped: the box size is
      // injected into the editor DATA as a span instead (CKEDitorComponent
      // seedData), so a size picked inside CKEditor renders single-layer — the
      // wrapper's em would otherwise multiply with it (~29% oversized until
      // close, then "snapping back" on save).
      textareaStyle.fontSize = toolbar.state.textFontSize / 14 + "em";
    }
    if (toolbar.state.textColor) {
      textareaStyle.color = toolbar.state.textColor;
    }
    if (toolbar.state.highlightColor) {
      textareaStyle.backgroundColor = toolbar.state.highlightColor;
    }
    if (toolbar.state.bold) {
      textareaStyle.fontWeight = "bold";
    }
    if (toolbar.state.italic) {
      textareaStyle.fontStyle = "italic";
    }
    if (toolbar.state.underline) {
      textareaStyle.textDecoration = "underline";
    }
    const isSelected = isBoxSelected(
      this.props.id,
      this.props.boxSelected,
      this.props.boxSelectedAll
    );
    let wholeBoxStyle = {
      position: box.position.type,
      left: box.position.x ? box.position.x : "",
      top: box.position.y ? box.position.y : "",
      // `max-content` (instead of `auto`) so the box keeps its natural
      // width when positioned past the parent's right edge — otherwise.
      // EXCEPTION: assessment question/answer text fills its grid cell, so the
      // WHOLE cell is the inline-edit click target. With max-content the box was
      // only as wide as the text (~38% of the cell), leaving a dead zone that
      // selected the outer box instead of editing — the "must click right on the
      // letters / can't edit on the 2nd click" bug.
      width:
        width !== "auto" ? width + widthUnit : this.isNestedInAssessment() ? "100%" : "max-content",
      maxWidth: width === "auto" ? "100%" : undefined,
      height: height !== "auto" ? height + heightUnit : "auto",
      touchAction: "none",
      msTouchAction: "none",
      transformOrigin: "center",
      cursor: vis ? "inherit" : "default", // esto evita que aparezcan los cursores de move y resize cuando la caja no está seleccionada
      // Selection lift: the selected box floats above its siblings so whatever
      // the user picked (canvas, Layers panel or a timeline row) is actually
      // visible instead of buried under a full-bleed layer. VIEW-ONLY — nothing
      // is written to `.boxes`, so the authored stacking order is untouched and
      // the lift disappears the moment the box is deselected. The trade-off,
      // and the reason this was once removed: while a box stays selected the
      // canvas shows it in front even if the Layers panel puts it behind.
      zIndex: boxZIndex(this.props.id, isSelected),
    };

    let rotate = "rotate(0deg)";
    if (!(this.props.markCreatorId && this.props.id === this.props.boxSelected)) {
      if (toolbar.structure.rotation && toolbar.structure.rotation) {
        rotate = "rotate(" + toolbar.structure.rotation + "deg)";
      }

      if (toolbar.structure.horizontalFlip) {
        rotate += " scaleX(-1)";
      }

      if (toolbar.structure.verticalFlip) {
        rotate += " scaleY(-1)";
      }
    }
    wholeBoxStyle.WebkitTransform =
      wholeBoxStyle.MozTransform =
      wholeBoxStyle.msTransform =
      wholeBoxStyle.OTransform =
      wholeBoxStyle.transform =
        rotate;

    let props = {
      ...this.props,
      marks: marks,
      allMarks: this.props.marks,
      update: (key, value) => {
        this.props.onToolbarUpdated(this.props.id, "main", "state", key, value);
      },
      parentBox: this.props.boxes[this.props.id],
      setCorrectAnswer: (correctAnswer) => {
        if (this.props.exercises.correctAnswer !== correctAnswer) {
          this.props.setCorrectAnswer(this.props.id, correctAnswer, this.props.page);
        }
      },
      // Inline "+ Add answer" / per-row "×" for assessment editor templates,
      // bound to this box id (callbacks come from getEditorCanvasProps).
      // Bottom "+ Cevap ekle" button intentionally disabled across all question
      // types — pass undefined so no plugin template renders it. Row "×" remove
      // and up/down reorder stay active.
      onAddAnswer: undefined,
      onRemoveAnswer: (i) =>
        this.props.onRemoveAnswer && this.props.onRemoveAnswer(this.props.id, i),
      // Grip-drag reorder (AtomicBox style): the template puts a sort handle on
      // All assessment types reorder via click up/down buttons (no drag grip):
      // dir = -1 up / +1 down. Routes to applyAnswerMove (MC/MA/Ordering/DragDrop)
      // or applyMatchingRightSwap (Matching) in EditorApp.reorderAnswersForBox.
      onMoveAnswer: this.props.onReorderAnswers
        ? (index, dir) => this.props.onReorderAnswers(this.props.id, index, index + dir)
        : undefined,
    };
    let template = IdealStudio.Plugins.get(toolbar.pluginId).getRenderTemplate(
      toolbar.state,
      props
    );
    // Ensure template is a valid string for html2json
    let templateString = template;
    if (template == null || typeof template !== "string") {
      templateString = "<p></p>";
    }
    let content;
    // Box-level text formatting (BasicText pill: font family/size, color,
    // highlight, B/I/U, align) — built once and shared by the display wrapper
    // AND the inline editor, so entering edit mode is visually seamless.
    // `fontScale` is legacy (%); `textFontSize` always overrides it. All text
    // scales with the canvas em base — same em reference CKEditor uses (see
    // the textareaStyle mirror above for why px is gone).
    const textFormatStyle = {
      ...(toolbar.state.fontScale
        ? { fontSize: Math.round(toolbar.state.fontScale * 100) + "%" }
        : {}),
      ...(toolbar.state.textAlign ? { textAlign: toolbar.state.textAlign } : {}),
      ...(toolbar.state.textFontFamily ? { fontFamily: toolbar.state.textFontFamily } : {}),
      ...(toolbar.state.textFontSize ? { fontSize: toolbar.state.textFontSize / 14 + "em" } : {}),
      ...(toolbar.state.textColor ? { color: toolbar.state.textColor } : {}),
      ...(toolbar.state.highlightColor ? { backgroundColor: toolbar.state.highlightColor } : {}),
      ...(toolbar.state.bold ? { fontWeight: "bold" } : {}),
      ...(toolbar.state.italic ? { fontStyle: "italic" } : {}),
      ...(toolbar.state.underline ? { textDecoration: "underline" } : {}),
    };
    // Same classes as the display wrapper — passed to the inline editor too.
    const textBoxClassName =
      "boxStyle " +
      classNames +
      (toolbar.state.textAlign ? " boxTextAlign-" + toolbar.state.textAlign : "");
    const isHtmlState = toolbar.state._html !== undefined && toolbar.state._html !== null;
    if (isHtmlState) {
      let htmlValue = toolbar.state._html;
      const isUrl =
        typeof htmlValue === "string" &&
        (htmlValue.startsWith("http://") || htmlValue.startsWith("https://"));
      const isFullHtmlDoc =
        typeof htmlValue === "string" &&
        (htmlValue.includes("<script") || htmlValue.includes("<html"));

      if (isUrl) {
        content = (
          <div
            style={{ ...style, padding: 0, overflow: "hidden" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
          >
            <div className="iframe-drag-handle">
              <LuMove size={18} color="white" />
            </div>
            <iframe
              src={htmlValue}
              style={{ width: "100%", height: "100%", border: "none", display: "block" }}
              sandbox={
                // allow-same-origin only for foreign origins: a same-origin author URL
                // would otherwise get full access to the host page/session.
                isForeignOrigin(htmlValue)
                  ? "allow-scripts allow-same-origin allow-popups"
                  : "allow-scripts allow-popups"
              }
              title="HTML content"
            />
          </div>
        );
      } else if (isFullHtmlDoc) {
        let secureHtmlValue = htmlValue;
        // Apply strict CSP and tight sandbox to untrusted/custom HTML
        secureHtmlValue = withHtmlCsp(this.props.id, htmlValue);
        const sandboxRules = "allow-scripts";

        content = (
          <div
            style={{ ...style, padding: 0, overflow: "hidden" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
          >
            <div className="iframe-drag-handle">
              <LuMove size={18} color="white" />
            </div>
            <ScaledCanvasFrame>
              <iframe
                srcDoc={secureHtmlValue}
                style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                sandbox={sandboxRules}
                onLoad={agentNavGuard(this.props.id, secureHtmlValue)}
                title="HTML content"
              />
            </ScaledCanvasFrame>
          </div>
        );
      } else {
        const secureHtmlValue = DOMPurify.sanitize(htmlValue);
        content = (
          <div
            style={{ ...style, padding: 0, overflow: "auto", width: "100%", height: "100%" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
            dangerouslySetInnerHTML={{ __html: secureHtmlValue }}
          />
        );
      }
    } else {
      content =
        config.flavor === "react" ? (
          <div
            style={style}
            {...attrs}
            // AtomicBox (smart Heading/Text/etc.) renders via ThemeBridge; its
            // Title/Text UI set their OWN inline text-align, which beats a plain
            // wrapper textAlign. The boxTextAlign-* SCSS uses !important so it
            // overrides those inline defaults for the whole subtree.
            className={
              "boxStyle " +
              classNames +
              (toolbar.state.textAlign ? " boxTextAlign-" + toolbar.state.textAlign : "")
            }
            ref={this.contentRef}
          >
            {template}
          </div>
        ) : (
          <div
            style={{ ...style, ...textFormatStyle }}
            {...attrs}
            // boxTextAlign-* forces alignment onto inner <p>/<span> that may
            // carry their own inline text-align (SCSS uses !important).
            className={textBoxClassName}
            ref={this.contentRef}
          >
            {this.renderChildren(html2json(templateString))}
          </div>
        );
    }
    let border = (
      <div style={{ visibility: vis ? "visible" : "hidden" }}>
        <div
          style={{
            position: "absolute",
            top: -this.borderSize,
            left: -this.borderSize,
            width: "100%",
            height: "100%",
            boxSizing: "content-box",
            // This full-size selection overlay otherwise SWALLOWS clicks once the
            // box is selected — which is why moving (the top handle) and inline
            // editing worked while UNselected but died once selected. Let clicks
            // pass through to the drag handle / answer text on assessment boxes.
            pointerEvents:
              config.category === "evaluation" || this.isNestedInAssessment() ? "none" : undefined,
          }}
        />
        <div style={{ zIndex: 9999, display: "initial" /* box.resizable ? 'initial' : 'none'*/ }}>
          {/* Plugins flagged notResizable (e.g. full-screen GuideMultiInput
              overlay) hide their corner handles so the box can't be resized.
              Assessment question/answer text is edit-only: no resize handles. */}
          {!config.notResizable &&
            !this.isNestedInAssessment() &&
            toolbar.pluginId !== "InteractivePlugin" && (
              <>
                <div
                  className="helpersResizable"
                  data-corner="nw"
                  onPointerDown={(e) => this.grabResizeHandle("nw", e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    left: -cornerSize / 2,
                    top: -cornerSize / 2,
                    width: cornerSize,
                    height: cornerSize,
                    cursor:
                      "nw-resize" /* (!isSortableContainer(box.container) ? 'nw-resize' : 'move')*/,
                  }}
                />
                <div
                  className="helpersResizable"
                  data-corner="ne"
                  onPointerDown={(e) => this.grabResizeHandle("ne", e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    right: -cornerSize / 2,
                    top: -cornerSize / 2,
                    width: cornerSize,
                    height: cornerSize,
                    cursor:
                      "ne-resize" /* (!isSortableContainer(box.container) ? 'ne-resize' : 'move')*/,
                  }}
                />
                <div
                  className="helpersResizable"
                  data-corner="sw"
                  onPointerDown={(e) => this.grabResizeHandle("sw", e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    left: -cornerSize / 2,
                    bottom: -cornerSize / 2,
                    width: cornerSize,
                    height: cornerSize,
                    cursor:
                      "sw-resize" /* (!isSortableContainer(box.container) ? 'sw-resize' : 'move')*/,
                  }}
                />
                <div
                  className="helpersResizable"
                  data-corner="se"
                  onPointerDown={(e) => this.grabResizeHandle("se", e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    right: -cornerSize / 2,
                    bottom: -cornerSize / 2,
                    width: cornerSize,
                    height: cornerSize,
                    cursor:
                      "se-resize" /* (!isSortableContainer(box.container) ? 'se-resize' : 'move')*/,
                  }}
                />
                {/* Bottom-edge grab strip: full-width ns-resize hit area with a
                    centered pill so vertical resizing is discoverable. Inset by
                    cornerSize so the corner handles keep their diagonal zones. */}
                <div
                  className="helpersResizable resizeEdgeBottom"
                  data-corner="s"
                  onPointerDown={(e) => this.grabResizeHandle("s", e)}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={{
                    left: cornerSize,
                    right: cornerSize,
                    bottom: -5,
                    height: 10,
                    cursor: "ns-resize",
                  }}
                >
                  <div className="resizeEdgePill" />
                </div>
              </>
            )}
          {/* Full-screen overlays are pinned to the scene: no rotation. And
              assessment question/answer text is edit-only: no rotate handle
              (it also has no onBoxRotate wired, which would otherwise throw). */}
          {!config.fullScreenOverlay &&
            !this.isNestedInAssessment() &&
            toolbar.pluginId !== "InteractivePlugin" && (
              // The handle group orbits WITH the asset normally. But once the box
              // rotation pushes it into the top half (90°–270°), we add a 180°
              // relative flip so the handle snaps to the screen-bottom half again.
              // At the left/right boundaries the flip turns off and it resumes
              // rotating with the asset.
              <div
                className="rotateHandleGroup"
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  transformOrigin: "center",
                  transform:
                    "rotate(" +
                    (Math.abs(normalizeRotation(toolbar.structure.rotation)) > 90 ? 180 : 0) +
                    "deg)",
                }}
              >
                <div className="rotateHandleLine" />
                <div
                  className="rotateHandle"
                  style={{ pointerEvents: "auto" }}
                  onMouseDown={this.handleRotateStart}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ pointerEvents: "none" }}
                  >
                    <path d="M21 2v6h-6" />
                    <path d="M21 13a9 9 0 1 1-3-7.7L21 8" />
                  </svg>
                </div>
                <div className="rotateDegreeLabel">
                  {Math.round(toolbar.structure.rotation || 0)}°
                </div>
              </div>
            )}
        </div>
      </div>
    );

    let classes = "wholebox";
    classes += " dnd"; // + box.container;
    // InteractivePlugin is a CONTAINER — its inline sub-items render their own
    // selection chrome via ItemFrame. Suppressing the parent-box dashed border
    // and corner handles keeps the canvas clean while a sub-item is being edited.
    const isInteractiveContainer = toolbar.pluginId === "InteractivePlugin";
    if (
      this.props.boxSelectedAll !== undefined &&
      this.props.boxSelectedAll != -1 &&
      this.props.boxSelectedAll.includes(this.props.id)
    ) {
      if (!isInteractiveContainer) classes += " selectedBox";
    } else if (this.props.id === this.props.boxSelected) {
      if (!isInteractiveContainer) classes += " selectedBox";
    }
    if (box.height === "auto") {
      classes += " automaticallySizedBox";
    }

    let showOverlay = "none";
    let verticalAlign = "top";

    if (this.props.isFibQuestion !== undefined && this.props.isFibQuestion) {
      let parts = stripParagraphTags(safeDecodeURI(toolbar.state.__text)).split(/#----(.*?)----#/);
      const fibCorrect =
        this.props.exercises && Array.isArray(this.props.exercises.correctAnswer)
          ? this.props.exercises.correctAnswer
          : [];
      for (let i = 1; i < parts.length; i += 2) {
        const blankIdx = (i - 1) / 2;
        parts[i] = (
          <input
            key={"fib-" + blankIdx}
            type="text"
            style={{ margin: "4px 6px", minWidth: "160px", padding: "6px 10px" }}
            className="fibinput"
            id={"fib-" + blankIdx}
            defaultValue={fibCorrect[blankIdx] || ""}
            placeholder={IdealStudio.i18n.t("AssessmentMenu.correctAnswerPlaceholder")}
            // Click straight into the blank to edit its correct answer. The parent
            // assessment box's onMouseDown calls preventDefault (blocks native focus)
            // and routes clicks into the inline editor — stop both here so the input
            // keeps focus and the caret lands where clicked.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => this.fibOnChangeHandler(e)}
          />
        );
      }
      content = <p>{parts}</p>;
    }

    if (this.props.isDndQuestion !== undefined && this.props.isDndQuestion) {
      const dndHtml = stripParagraphTags(safeDecodeURI(toolbar.state.__text));
      const dndParts = splitSlots(dndHtml).map((part, k) =>
        part.type === "slot" ? (
          <span key={"slot-" + k} className="dnd-slot-chip" aria-hidden="true">
            {part.index}
          </span>
        ) : (
          <span
            key={"txt-" + k}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(part.value) }}
          />
        )
      );
      content = <p>{dndParts}</p>;
    }

    wholeBoxStyle.verticalAlign = verticalAlign;

    const isInMultiSelect =
      Array.isArray(this.props.boxSelectedAll) && this.props.boxSelectedAll.includes(this.props.id);

    if (isInMultiSelect) {
      // Outline only — no lift, same reason as the single-selection case above.
      wholeBoxStyle = {
        ...wholeBoxStyle,
        outline: "2px dashed #5b8ef5",
        outlineOffset: "2px",
      };
    }

    // In DISPLAY mode, assessment question/answer text must not be text-
    // selectable. Otherwise a stray drag-select on the words eats the click, so
    // 1-click inline edit only fired on the non-text padding ("the gap between
    // the text and the number/letter"). Disabling selection makes the whole box
    // a reliable click target; the inline editor re-enables selection while editing.
    if (this.isNestedInAssessment() && !this.state.inlineEditing) {
      wholeBoxStyle = {
        ...wholeBoxStyle,
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        // Signal "click to edit", not "drag me". Without this the box inherits
        // the parent assessment box's move/grab cursor (the parent is draggable),
        // which both looks wrong and let a press-with-jitter start dragging the
        // PARENT — swallowing the click so inline edit only fired sometimes.
        cursor: "text",
      };
    }

    // Auto-scale only content-driven (height:auto) question boxes; skip
    // fixed-height/iframe/nested ones (see ExerciseAutoFit).
    const isExerciseBox =
      config.category === "evaluation" &&
      height === "auto" &&
      !isSortableContainer(box.container) &&
      !isHtmlState;

    return (
      <div
        className={classes}
        id={"box-" + this.props.id}
        name={toolbar.pluginId}
        ref={this.nodeRef}
        // Marks assessment question/answer text so the PARENT assessment box's
        // interact.draggable ignores pointerdowns here (ignoreFrom) — otherwise a
        // press-with-jitter on the text dragged the parent and ate the click.
        {...(this.isNestedInAssessment() ? { "data-assessment-text": "true" } : {})}
        // BasicText/RichText: suppress .selectedBox's yellow fill + forced text
        // color (see _editorBox.scss) so the pill's own textColor/highlightColor
        // stay visible while selected.
        {...(config.needsTextEdition ? { "data-text-plugin": "true" } : {})}
        onContextMenu={this.handleContextMenu}
        onMouseDown={(e) => {
          // Decide selection on mousedown so it can't race with a drag's onstart.
          // Shift on a resize handle is the aspect-lock shortcut, not multi-select.
          const onResizeHandle = !!(e.target.closest && e.target.closest(".helpersResizable"));
          this._modifierAtDown = (e.metaKey || e.ctrlKey || e.shiftKey) && !onResizeHandle;
          if (this._modifierAtDown) {
            this.props.onBoxSelectedToggle(this.props.id, this.props.boxSelected);
            e.stopPropagation();
            return;
          }
          // CKEditor open on this nested text: let the press through to the
          // editable (caret placement / drag-select) but STOP it from bubbling
          // to the parent assessment box. Without this the parent's mousedown
          // selected ITSELF, boxSelected moved away, and CKEDitorComponent's
          // deselect auto-save closed the editor mid-selection.
          if (
            config &&
            config.needsTextEdition &&
            this.isNestedInAssessment() &&
            toolbar.showTextEditor
          ) {
            e.stopPropagation();
            return;
          }
          // Assessment question/answer text → enter inline edit on PRESS, not on
          // a clean click. A click needs zero movement; the tiniest drag turned
          // it into a box "move" and ate the edit (the recurring "won't edit the
          // 2nd time / enters move mode" bug). Firing here + stopPropagation also
          // stops the parent assessment box from claiming the press as a drag.
          // The inline editor auto-focuses on mount, so the caret lands correctly.
          if (
            config &&
            config.needsTextEdition &&
            this.isNestedInAssessment() &&
            !this.state.inlineEditing &&
            // CKEditor (rich) zaten açıksa metne tıklamak onu kapatmasın —
            // kapatma yalnızca "Kaydet" ile. Aksi halde tıklama/seçim editörü
            // kapatıp kaymaya yol açıyordu.
            !toolbar.showTextEditor
          ) {
            // CRITICAL: stop the browser's native mousedown focus. Otherwise the
            // press focuses the box AFTER the inline editor mounts + auto-focuses,
            // so the editor immediately blurs → auto-commits → closes. That's the
            // real-mouse-only failure (synthetic events don't trigger native focus,
            // which is why it "worked" in tests but never for a real click).
            e.preventDefault();
            if (this.props.id !== this.props.boxSelected) this.props.onBoxSelected(this.props.id);
            if (toolbar.showTextEditor) this.props.onTextEditorToggled(this.props.id, false);
            this.setState({ inlineEditing: true });
            e.stopPropagation();
            return;
          }
          // Select only if not already selected, so grabbing a group member keeps the group.
          const inCurrentSelection =
            this.props.boxSelected === this.props.id ||
            (Array.isArray(this.props.boxSelectedAll) &&
              this.props.boxSelectedAll.includes(this.props.id));
          if (!inCurrentSelection) {
            this.props.onBoxSelected(this.props.id);
          }
        }}
        onClick={(e) => {
          const isMultiKey = e.metaKey || e.ctrlKey || e.shiftKey;

          if (isMultiKey) {
            e.stopPropagation(); // toggle handled on mousedown
            return;
          }

          // 1-click smooth inline edit for assessment question/answer text — no
          // double-click, no CKEditor load. DragDrop / FillInBlank questions
          // edit inline too (their slots/blanks render as atomic chips).
          if (
            config &&
            config.needsTextEdition &&
            this.isNestedInAssessment() &&
            !this.state.inlineEditing &&
            // CKEditor (rich) zaten açıksa metne tıklamak onu kapatmasın —
            // kapatma yalnızca "Kaydet" ile. Aksi halde tıklama/seçim editörü
            // kapatıp kaymaya yol açıyordu.
            !toolbar.showTextEditor
          ) {
            if (this.props.id !== this.props.boxSelected) this.props.onBoxSelected(this.props.id);
            // If a CKEditor is (or got stuck) open on this box, close it so the
            // gate can't be permanently blocked — fixes "can't inline-edit again
            // after the first time", incl. after save+refresh with showTextEditor
            // persisted true.
            if (toolbar.showTextEditor) this.props.onTextEditorToggled(this.props.id, false);
            this.setState({ inlineEditing: true });
            e.stopPropagation();
            return;
          }

          if (this.props.boxSelected !== this.props.id) {
            // Do not stop propagation if we are not allowed to select this box because of its level, so it selects the parent instead of itself
            if (
              !isAncestorOrSibling(this.props.boxSelected, this.props.id, this.props.boxes) &&
              isBox(box.parent)
            ) {
              return;
            }
            // If it is a box inside another box, you are only allowed to select it if you have its parent box selected.
            // Also allow selection when parent is not a box (i.e. direct slide child) regardless of stored level,
            // since imported state can produce inflated level values for top-level boxes.
            if (
              box.level < 1 ||
              box.level < this.props.boxLevelSelected ||
              isAncestorOrSibling(this.props.boxSelected, this.props.id, this.props.boxes) ||
              !isBox(box.parent)
            ) {
              this.props.onBoxSelected(this.props.id);
            }
          }
          e.stopPropagation();
        }}
        onDoubleClick={(e) => {
          if (!config || !config.needsTextEdition) return;
          // Assessment question/answer text uses 1-click inline edit (handled in
          // onClick); no double-click-to-edit there.
          if (this.isNestedInAssessment()) return;
          if (this.props.id === this.props.boxSelected) {
            // Standalone text: double-click opens the same lightweight inline
            // editor questions use (pill stays for formatting) — the full
            // CKEditor opens ONLY from the right rail's "Metni Düzenle".
            // When CKEditor IS already open, do nothing: the double-click is
            // word-selection inside its editable. "RichText" here is the
            // legacy alias of BasicText (see plugins/RichText) — old boxes
            // behave identically.
            if (toolbar.pluginId === "BasicText" || toolbar.pluginId === "RichText") {
              if (!toolbar.showTextEditor && !this.state.inlineEditing) {
                this.setState({ inlineEditing: true });
              }
            } else {
              this.props.onTextEditorToggled(this.props.id, true);
            }
            e.stopPropagation();
          }
        }}
        style={wholeBoxStyle}
        // Imperative animation resets restore this instead of "".
        data-base-transform={rotate === "rotate(0deg)" ? undefined : rotate}
      >
        {border}
        {config.category === "evaluation" ? (
          <div className="assessment-drag-handle" title="Drag to move" aria-hidden="true">
            <span className="assessment-drag-handle__grip" />
          </div>
        ) : null}
        {toolbar.showTextEditor ? null : config.needsTextEdition && this.state.inlineEditing ? (
          <InlineHtmlEditor
            html={toolbar.state.__text}
            // Same wrapper style/classes as display mode → no visual jump when
            // the click swaps the rendered text for the contentEditable.
            style={{ ...style, ...textFormatStyle }}
            className={textBoxClassName}
            dndQuestion={!!this.props.isDndQuestion}
            fibQuestion={!!this.props.isFibQuestion}
            onCommit={(h) => this.commitInlineEdit(h)}
            onCancel={() => this.cancelInlineEdit()}
            onAddDragArea={
              (this.props.isDndQuestion || this.props.isFibQuestion) && this.props.onAddDragArea
                ? (h, insertIndex) => this.props.onAddDragArea(this.props.id, h, insertIndex)
                : undefined
            }
            fibAnswers={
              this.props.isFibQuestion && this.props.getFibAnswers
                ? this.props.getFibAnswers(box.parent)
                : null
            }
            onSetFibAnswer={
              this.props.isFibQuestion && this.props.onSetFibAnswer
                ? (idx, value) => this.props.onSetFibAnswer(box.parent, idx, value)
                : undefined
            }
          />
        ) : isExerciseBox ? (
          <ExerciseAutoFit>{content}</ExerciseAutoFit>
        ) : (
          content
        )}
        {toolbar.state.__text && !isHtmlState ? (
          <CKEDitorComponent
            key={"ck-" + this.props.id}
            boxSelected={this.props.boxSelected}
            box={this.props.boxes[this.props.id]}
            style={textareaStyle}
            className={classNames + " textAreaStyle"}
            toolbars={this.props.pluginToolbars}
            id={this.props.id}
            onBlur={this.blurTextarea}
          />
        ) : null}
        <div className="boxOverlay" style={{ display: showOverlay }} />
        <MarkCreator
          addMarkShortcut={this.props.addMarkShortcut}
          deleteMarkCreator={this.props.deleteMarkCreator}
          onBoxAdded={this.props.onBoxAdded}
          boxSelected={this.props.boxSelected}
          containedViews={this.props.containedViews}
          toolbar={toolbar ? toolbar : {}}
          parseRichMarkInput={IdealStudio.Plugins.get(toolbar.pluginId).parseRichMarkInput}
          markCreatorId={this.props.markCreatorId}
          currentId={this.props.id}
          pageType={this.props.pageType}
          onRichMarksModalToggled={this.props.onRichMarksModalToggled}
        />
        {this.props.dndIndex != null ? (
          <span
            aria-hidden="true"
            title={"Drop slot " + this.props.dndIndex}
            style={{
              position: "absolute",
              bottom: "6%",
              right: "6%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "1.6em",
              height: "1.6em",
              padding: "0 0.45em",
              borderRadius: "9999px",
              backgroundColor: "#ede9fe",
              color: "#5b21b6",
              fontSize: "0.72em",
              fontWeight: 700,
              lineHeight: 1,
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.15)",
            }}
          >
            {this.props.dndIndex}
          </span>
        ) : null}
        <AideaSmartElementsOverlay boxId={this.props.id} />
        {this.state.contextMenu ? (
          <BoxContextMenu
            boxId={this.props.id}
            position={this.state.contextMenu}
            onClose={this.closeContextMenu}
          />
        ) : null}
      </div>
    );
  }

  /**
   * Renders box children
   * @param markup Content
   * @param key Unique React key
   * @returns {children} React components for the box children
   */
  renderChildren(markup, key) {
    let component;
    let props = {};
    let children = null;
    switch (markup.node) {
      case "element":
        if (markup.attr) {
          props = markup.attr;
          // Convert 'class' to 'className' for React compatibility
          if (props.class) {
            props.className = props.class;
            delete props.class;
          }
        }
        props.key = key;
        if (markup.tag === "plugin") {
          component = PluginPlaceholder;
          let resizable = markup.attr.hasOwnProperty("plugin-data-resizable");
          props = Object.assign({}, props, {
            pluginContainer: markup.attr["plugin-container"],
            resizable: resizable,
            parentBox: this.props.boxes[this.props.id],
          });
        } else {
          component = markup.tag;
        }
        break;
      case "text":
        component = "span";
        props = { key: key };
        children = [safeDecodeURI(markup.text)];
        break;
      case "root":
        component = "div";
        props = { style: { width: "100%", height: "100%" } };
        break;
    }

    Object.keys(props).forEach((prop) => {
      if (prop.startsWith("on")) {
        let value = props[prop];
        if (typeof value === "string") {
          props[prop] = function () {};
        }
      }
      // Convert style to object - handle string, array, or object formats
      if (prop === "style") {
        let styleValue = props[prop];
        let styleObj = {};

        const parseStyleString = (styleString) => {
          const normalizedStyle = normalizeFontSizes(styleString);
          const parsed = {};
          normalizedStyle.split(";").forEach((rule) => {
            let trimmedRule = rule.trim();
            if (trimmedRule) {
              let [property, value] = trimmedRule.split(":").map((s) => s.trim());
              if (property && value) {
                let camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                parsed[camelProperty] = value;
              }
            }
          });
          return parsed;
        };

        // If style is an array (html2json default format)
        if (Array.isArray(styleValue)) {
          styleValue = styleValue.join(" ");
          styleObj = parseStyleString(styleValue);
          props[prop] = styleObj;
        } else if (typeof styleValue === "string") {
          styleObj = parseStyleString(styleValue);
          props[prop] = styleObj;
        } else if (
          typeof styleValue === "object" &&
          styleValue !== null &&
          !Array.isArray(styleValue)
        ) {
          const needsParsing = Object.values(styleValue).some(
            (val) => typeof val === "string" && val.includes(":")
          );

          if (needsParsing) {
            Object.keys(styleValue).forEach((styleKey) => {
              // Skip numeric keys, but parse their string values if they contain style rules
              if (typeof styleValue[styleKey] === "string" && styleValue[styleKey].includes(":")) {
                const parsed = parseStyleString(styleValue[styleKey]);
                Object.assign(styleObj, parsed);
              } else if (isNaN(styleKey)) {
                styleObj[styleKey] = styleValue[styleKey];
              }
            });
          } else {
            // Object with valid style properties, just clean numeric keys (React 19 fix)
            Object.keys(styleValue).forEach((styleKey) => {
              // Only keep non-numeric string keys
              if (isNaN(styleKey)) {
                styleObj[styleKey] = styleValue[styleKey];
              }
            });
          }
          props[prop] = styleObj;
        }
      }
    });

    if (markup.child) {
      if (markup.child.length === 1 && markup.child[0].node === "text") {
        props.dangerouslySetInnerHTML = {
          __html: DOMPurify.sanitize(
            normalizeFontSizes(safeDecodeURI(markup.child[0].text)),
            SAFE_LINK_CONFIG
          ),
        };
        children = null;
      } else {
        children = [];
        markup.child.forEach((child, index) => {
          children.push(this.renderChildren(child, index));
        });
      }
    }

    // Ensure we don't pass both dangerouslySetInnerHTML and children
    if (props.dangerouslySetInnerHTML) {
      return React.createElement(component, props);
    } else {
      return React.createElement(component, props, children);
    }
  }

  /**
   * Blurs text area and saves data
   */
  blurTextarea(text, data) {
    this.props.onTextEditorToggled(this.props.id, false, text, data);
  }

  /**
   * Checks if aspect ratio should be kept when resizing the box
   * @returns {boolean} true if aspect ratio shoud be kept, false otherwise
   */
  checkAspectRatioValue() {
    let toolbar = this.props.pluginToolbars[this.props.id];
    let apiPlugin = getPluginSafe(toolbar.pluginId);
    if (!apiPlugin) return false;
    let config = apiPlugin.getConfig();
    let box = this.props.boxes[this.props.id];
    if (
      box &&
      toolbar &&
      toolbar.structure &&
      toolbar.structure.aspectRatio === true
      /* &&( toolbar.structure.height === 'auto' || toolbar.structure.width === 'auto')*/
    ) {
      return true;
    }

    if (config.aspectRatioButtonConfig) {
      let arb = config.aspectRatioButtonConfig;
      if (arb.location.length === 2) {
        let comp = toolbar.structure.aspectRatio;
        if (comp) {
          return comp.checked;
        }
        return false;
      }
      let comp = toolbar.structure.aspectRatio;
      if (comp) {
        return comp.checked;
      }
      return false;
    }

    return false;
  }

  /**
   * Effective aspect lock for a resize: the stored "Lock aspect ratio" toggle,
   * inverted while Shift is held (Figma/PowerPoint convention — Shift locks an
   * unlocked box, and frees a locked one).
   */
  isAspectLockActive() {
    return this.checkAspectRatioValue() !== !!this._shiftHeld;
  }

  /**
   * One aspectRatio modifier per box, toggled via options.enabled instead of
   * being added/removed. interact reads options.enabled on every move, and the
   * running interaction holds this same options object, so pressing/releasing
   * Shift mid-drag takes effect on the next pointer move.
   */
  getResizeModifiers() {
    if (!interact || !interact.modifiers || !interact.modifiers.aspectRatio) {
      return [];
    }
    if (!this._aspectModifier) {
      this._aspectModifier = interact.modifiers.aspectRatio({ ratio: "preserve" });
    }
    this._aspectModifier.options.enabled = this.isAspectLockActive();
    return [this._aspectModifier];
  }

  syncAspectLock() {
    if (!this.props.pluginToolbars?.[this.props.id]) return;
    const locked = this.isAspectLockActive();
    if (this._aspectModifier) {
      this._aspectModifier.options.enabled = locked;
    }
    // Rotated resize bypasses interact's modifiers (see _rotResize).
    if (this._rotResize) {
      this._rotResize.aspect = locked;
    }
  }

  setShiftHeld(held) {
    if (!!this._shiftHeld === held) return;
    this._shiftHeld = held;
    this.syncAspectLock();
  }

  grabResizeHandle(corner, e) {
    this._grabbedCorner = corner;
    this.setShiftHeld(!!e.shiftKey);
  }

  /**
   * Minimum size allowed when resizing the box, in on-screen pixels (so it
   * matches interact's scaled event.rect under slideScale).
   *
   * For text boxes the minimum is the size the content actually needs: the box
   * can't be shrunk below its text, which keeps the text from spilling outside
   * the box. Width is left free (text rewraps as it narrows) unless an
   * unbreakable line overflows horizontally; height is clamped to the rendered
   * text height. Non-text boxes only get a tiny floor so nothing collapses to 0.
   * @returns {{width:number, height:number}}
   */
  getResizeMin() {
    // interact calls this on every pointermove (often 120-240Hz); the content
    // measurements below force a reflow right after the move's style writes.
    // Layout can't change more than once per frame, so cache per frame — this
    // is what made shrinking a text box feel laggy.
    if (this._resizeMinCache) {
      return this._resizeMinCache;
    }
    const FLOOR = 25;
    const content = this.contentRef.current;
    const toolbar = this.props.pluginToolbars[this.props.id];
    const config = toolbar ? getPluginSafe(toolbar.pluginId)?.getConfig() || {} : {};
    let min;
    if (!config.needsTextEdition || !content) {
      min = { width: FLOOR, height: FLOOR };
    } else {
      // Measure the height the TEXT needs, not the wrapper's rendered height:
      // .boxStyle is height:100%, so once the box has a concrete height the
      // wrapper always fills it — measuring it as-is clamps every shrink to
      // the current height and the box can only creep down a few px per frame.
      // Temporarily collapse to auto so the measurement is the true content
      // height at the current width.
      const prevInlineHeight = content.style.height;
      content.style.height = "auto";
      const rect = content.getBoundingClientRect();
      // scrollWidth > clientWidth means a non-wrapping line overflows sideways.
      const horizontalOverflow = content.scrollWidth > content.clientWidth + 1;
      const scale = content.clientWidth ? rect.width / content.clientWidth : 1;
      content.style.height = prevInlineHeight;
      min = {
        width: Math.max(horizontalOverflow ? content.scrollWidth * scale : FLOOR, FLOOR),
        height: Math.max(rect.height, FLOOR),
      };
    }
    this._resizeMinCache = min;
    requestAnimationFrame(() => {
      this._resizeMinCache = null;
    });
    return min;
  }

  /**
   * Canvas zoom factor (slideScale) read live from the DOM. interact.js reports
   * pointer/rect/delta values in ON-SCREEN pixels, but #airlayer is CSS-scaled
   * by the zoom control, so a box's local style.width/height/translate live in
   * the UNSCALED space. Screen px must be divided by this before being written
   * to the box, otherwise at zoom ≠ 100% the box resizes/moves scale× too fast.
   * offsetWidth is the unscaled layout width; getBoundingClientRect().width
   * includes the scale, so their ratio is exactly slideScale.
   * @returns {number} scale (1 when unzoomed or DOM missing)
   */
  getCanvasScale() {
    const id = this.props.containedViewSelected === 0 ? "maincontent" : "contained_maincontent";
    const mc = document.getElementById(id);
    if (!mc || !mc.offsetWidth) return 1;
    const s = mc.getBoundingClientRect().width / mc.offsetWidth;
    return isFinite(s) && s > 0 ? s : 1;
  }

  /**
   * True when this box is a question/answer nested inside an assessment
   * (evaluation-category) box. Such boxes are click-to-inline-edit only —
   * never draggable in the editor: drag-to-reorder is replaced by the inline
   * menu buttons, and dragging a Matching answer used to crash. The learner
   * Visor render is separate, so this only affects authoring.
   */
  isNestedInAssessment() {
    const box = this.props.boxes[this.props.id];
    if (!box || !isBox(box.parent)) return false;
    const parentTb = this.props.pluginToolbars[box.parent];
    const parentPlugin = parentTb && IdealStudio.Plugins.get(parentTb.pluginId);
    return !!(parentPlugin && parentPlugin.getConfig().category === "evaluation");
  }

  /**
   * After component updates
   * @param prevProps React previous props
   * @param prevState React previous state
   */
  componentDidUpdate(prevProps, prevState) {
    let toolbar = this.props.pluginToolbars[this.props.id];
    let box = this.props.boxes[this.props.id];
    let node = this.nodeRef.current;

    if (!node) {
      return;
    }
    if (!getPluginSafe(toolbar.pluginId)) return;
    let config = IdealStudio.Plugins.get(toolbar.pluginId).getConfig();
    let offsetEl = document.getElementById("maincontent")
      ? document.getElementById("maincontent").getBoundingClientRect()
      : {};
    let leftO = offsetEl.left || 0;
    let topO = offsetEl.top || 0;
    // Resize uses interact's native grid snap; drag uses the alignment-
    // guide system in onmove/onend and explicitly disables native snap.
    let gridTarget = interact.snappers
      ? interact.snappers.grid({
          x: SNAP_DRAG,
          y: SNAP_DRAG,
          range: Math.floor(SNAP_DRAG / 2),
          offset: { x: leftO, y: topO },
        })
      : interact.createSnapGrid({
          x: SNAP_DRAG,
          y: SNAP_DRAG,
          range: Math.floor(SNAP_DRAG / 2),
          offset: { x: leftO, y: topO },
        });

    let snap = { targets: [], relativePoints: [{ x: 0, y: 0 }] };
    let snapSize = {};
    if (this.props.grid) {
      snap = { targets: [gridTarget], relativePoints: [{ x: 0, y: 0 }] };
      snapSize = {
        targets: [{ width: SNAP_SIZE, height: SNAP_SIZE, range: SNAP_SIZE }],
      };
    }

    const noDragSnap = { targets: [], relativePoints: [{ x: 0, y: 0 }] };
    if (
      prevProps.pluginToolbars[this.props.id] &&
      toolbar.showTextEditor !== prevProps.pluginToolbars[this.props.id].showTextEditor &&
      box.draggable
    ) {
      interact(node).draggable({
        enabled:
          !toolbar.showTextEditor && !config.fullScreenOverlay && !this.isNestedInAssessment(),
        snap: noDragSnap,
      });
    } else {
      interact(node).draggable({ snap: noDragSnap });
    }
    if (config.fullScreenOverlay) {
      // Pinned overlay: never draggable
      interact(node).draggable({ enabled: false });
    }

    // Assessment question/answer text is never draggable in the editor. Re-assert
    // on EVERY update: the mount-time gate can run before the parent toolbar is in
    // props (isNestedInAssessment() false then), and the conditional branch above
    // only re-evaluates `enabled` on a showTextEditor change — so a stale "enabled"
    // would otherwise let the text be dragged (and crash the dropzone ondrop).
    if (this.isNestedInAssessment()) {
      interact(node).draggable({ enabled: false });
    }

    // if (box.resizable) {
    interact(node).resizable({
      modifiers: this.getResizeModifiers(),
      snap: snap,
      snapSize: snapSize,
      enabled: !config.notResizable && !this.isNestedInAssessment(),
    });
    // }

    if (box.level > this.props.boxLevelSelected && this.props.boxLevelSelected > 0) {
      interact(node).draggable({ enabled: false });
    }

    // Locked boxes are never draggable/resizable on the canvas. Re-assert on
    // every update so toggling the Layers-panel lock takes effect immediately
    // (mirrors the isNestedInAssessment "never draggable" gate above).
    if (box.timeline?.locked) {
      interact(node).draggable({ enabled: false });
      interact(node).resizable({ enabled: false });
    }
  }

  /**
   * After component mounts
   * Set interact listeners for box manipulation
   */
  componentDidMount() {
    // Track Shift for the aspect-lock shortcut, including presses/releases in
    // the middle of a resize drag. Blur resets it so a Shift released outside
    // the window doesn't leave the lock inverted.
    this._onShiftKey = (e) => {
      if (e.type === "blur") this.setShiftHeld(false);
      else if (e.key === "Shift") this.setShiftHeld(e.type === "keydown");
    };
    window.addEventListener("keydown", this._onShiftKey);
    window.addEventListener("keyup", this._onShiftKey);
    window.addEventListener("blur", this._onShiftKey);

    let toolbar = this.props.pluginToolbars[this.props.id];
    let apiPlugin = getPluginSafe(toolbar.pluginId);
    if (!apiPlugin) return;
    let config = apiPlugin.getConfig();
    let box = this.props.boxes[this.props.id];
    let container = box.container;
    let offsetEl = document.getElementById("maincontent")
      ? document.getElementById("maincontent").getBoundingClientRect()
      : {};
    let leftO = offsetEl.left || 0;
    let topO = offsetEl.top || 0;
    let gridTarget = interact.snappers
      ? interact.snappers.grid({
          x: SNAP_DRAG,
          y: SNAP_DRAG,
          range: Math.floor(SNAP_DRAG / 2),
          offset: { x: leftO, y: topO },
        })
      : interact.createSnapGrid({
          x: SNAP_DRAG,
          y: SNAP_DRAG,
          range: Math.floor(SNAP_DRAG / 2),
          offset: { x: leftO, y: topO },
        });

    let targets = this.props.grid ? [gridTarget] : [];
    let dragTargets = []; // drag snap handled by alignment-guide system
    if (this.contentRef.current) {
      apiPlugin.afterRender(this.contentRef.current, toolbar.state);
    }
    // Drag bounded by the canvas col so element can sit in the dim area
    // around the slide but never leaves the visible canvas.
    let dragRestrictionSelector = isSortableContainer(box.container)
      ? ".scrollcontainer"
      : ".canvasSliClass";

    let resizeRestrictionSelector = isSortableContainer(box.container) ? "body" : ".canvasSliClass";
    interact.dynamicDrop(true);

    if (this.nodeRef.current) {
      interact(this.nodeRef.current)
        .draggable({
          snap: {
            targets: dragTargets,
            relativePoints: [{ x: 0, y: 0 }],
          },
          enabled:
            box.draggable &&
            !config.fullScreenOverlay &&
            !this.isNestedInAssessment() &&
            !box.timeline?.locked,
          restrict: {
            restriction: dragRestrictionSelector,
            elementRect: { top: 0, left: 0, bottom: 1, right: 1 },
          },
          autoScroll: false,
          // Assessment container boxes move ONLY via the top drag handle — the
          // body is for inline editing, so a click/press on the content never
          // starts a "move" (the repeated "2nd click enters move mode" bug).
          allowFrom: config.category === "evaluation" ? ".assessment-drag-handle" : undefined,
          ignoreFrom:
            "input, textarea, select, .textAreaStyle,  a,.pointerEventsEnabled, .markeditor, .atomicSortHandle, .assessment-reorder, .inline-html-editor, [data-answer-row], [data-assessment-text], .rotateHandleGroup, .rotateHandle, .rotateHandleLine",
          onstart: (event) => {
            // Modifier-click is a selection toggle (done on mousedown), not a drag.
            if (this._modifierAtDown) {
              this._modifierAtDown = false;
              if (event.interaction && event.interaction.stop) {
                event.interaction.stop();
              }
              return;
            }
            this._followers = [];
            event.stopPropagation();
            container = this.props.boxes[this.props.id].container;

            const containerId =
              this.props.containedViewSelected === 0 ? "maincontent" : "contained_maincontent";

            this._snapSession = {
              containerId,
              ...buildSnapTargets({
                boxes: this.props.boxes,
                draggedBoxId: this.props.id,
                containerId,
                pixelGrid: !!this.props.grid,
              }),
            };
            this._rafScheduled = false;
            this._pendingGuides = null;

            if (this.props.onDragStart) {
              this.props.onDragStart(this.props.id);
            }
            // If contained in smth different from ContainedCanvas (sortableContainer || PluginPlaceHolder), clone the node and hide the original
            if (isSortableContainer(box.container)) {
              let original = event.target;
              let parent = original;
              // Find real parent to append clone
              let iterate = true;
              while (iterate) {
                parent = parent.parentNode;
                if (
                  parent.className &&
                  (parent.className.indexOf("editorBoxSortableContainer") !== -1 ||
                    parent.className.indexOf("slide_air") !== -1)
                ) {
                  iterate = false;
                }
              }
              parent = document.body;
              // Clone, assign values and hide original
              let clone = original.cloneNode(true);
              original.style.WebkitTransform =
                original.style.MozTransform =
                original.style.msTransform =
                original.style.OTransform =
                original.style.transform =
                  "none";
              let originalRect = original.getBoundingClientRect();
              let parentRect = parent.getBoundingClientRect();
              let x = originalRect.left - parentRect.left;
              let y = originalRect.top - parentRect.top;
              let canvasElement = document.querySelector(".canvasSliClass");
              if (canvasElement) {
                let computedStyle = window.getComputedStyle(canvasElement);
                clone.style.fontSize = computedStyle.fontSize;
              }

              clone.setAttribute("id", "clone");
              clone.setAttribute("data-x", x);
              clone.setAttribute("data-y", y);
              clone.style.left = 0 + "px";
              clone.style.top = 0 + "px";
              clone.style.zIndex = "9999 !important";
              original.setAttribute("data-x", x);
              original.setAttribute("data-y", y);
              clone.style.position = "absolute";

              clone.style.height = original.offsetHeight + "px";
              clone.style.width = original.offsetWidth + "px";
              clone.style.border = "1px dashed #555";
              parent.appendChild(clone);
              original.style.opacity = 0;
              original.style.WebkitTransform =
                original.style.MozTransform =
                original.style.msTransform =
                original.style.OTransform =
                original.style.transform =
                  this.rotate();
            } else if (isContainedView(box.container)) {
              let target = event.target;
              target.style.left =
                this.getElementPositionFromLeft(
                  target.style.left,
                  target.parentElement.offsetWidth
                ) + "px";
              target.style.top =
                target.parentElement.offsetHeight * (parseFloat(target.style.top) / 100) + "px";
            } else {
              let target = event.target;
              let topInPix =
                target.parentElement.offsetHeight * (parseFloat(target.style.top) / 100);
              let leftInPix =
                target.parentElement.offsetWidth * (parseFloat(target.style.left) / 100);

              target.style.top = topInPix + "px";
              target.style.left = leftInPix + "px";

              // Stay in your own layer while dragging. Lifting the dragged box
              // (or the selected-box z-index bump) hid whatever sits IN FRONT
              // of it, so aligning against those elements was guesswork until
              // the drop put it back. 0 paints in tree order, like an
              // undragged box — same stacking as after the drop, no jump.
              target.style.zIndex = "0";

              // Leader-follower: prepare follower boxes for multi-drag
              const allSelected = this.props.boxSelectedAll;
              if (Array.isArray(allSelected) && allSelected.length > 1) {
                const containerId =
                  this.props.containedViewSelected === 0 ? "maincontent" : "contained_maincontent";
                const container = document.getElementById(containerId);
                if (container) {
                  allSelected.forEach((fid) => {
                    if (fid === this.props.id) return;
                    const fEl = document.getElementById("box-" + fid);
                    if (!fEl) return;
                    const fLeftPx = container.offsetWidth * (parseFloat(fEl.style.left) / 100);
                    const fTopPx = container.offsetHeight * (parseFloat(fEl.style.top) / 100);
                    fEl.style.left = fLeftPx + "px";
                    fEl.style.top = fTopPx + "px";
                    fEl.setAttribute("data-x", 0);
                    fEl.setAttribute("data-y", 0);
                    fEl.style.zIndex = "0";
                    this._followers.push({ id: fid, el: fEl });
                  });
                }
              }
            }
          },
          onmove: (event) => {
            event.stopPropagation();
            // Hide EditorShortcuts
            let bar =
              this.props.containedViewSelected === 0
                ? document.getElementById("editorBoxIcons")
                : document.getElementById("contained_editorBoxIcons");
            if (bar) {
              bar.classList.add("hidden");
            }
            setBoxPillsVisible(false);

            // Level has to be the same to drag a box, unless a sortableContainer is selected, then it should allow level 0 boxes
            if (
              box.level - this.props.boxLevelSelected === 0 ||
              (box.level === 0 && this.props.boxLevelSelected < 1)
            ) {
              // If box not in a sortableContainer or PluginPlaceHolder, just drag
              if (!isSortableContainer(box.container)) {
                let target = event.target;

                let baseX = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
                let baseY = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;
                let currentPos = { x: baseX, y: baseY };
                let guidesForThisFrame = [];

                if (this._snapSession && this._snapSession.containerRect) {
                  const containerRect = this._snapSession.containerRect;
                  const targetRect = target.getBoundingClientRect();
                  // Candidate = current DOM rect + this frame's mouse delta.
                  const draggedRect = {
                    left: targetRect.left - containerRect.left + event.dx,
                    top: targetRect.top - containerRect.top + event.dy,
                    width: targetRect.width,
                    height: targetRect.height,
                  };
                  const { dx, dy, activeGuides } = resolveSnap({
                    draggedRect,
                    snapData: this._snapSession,
                  });
                  currentPos.x = baseX + dx;
                  currentPos.y = baseY + dy;
                  guidesForThisFrame = activeGuides;
                }

                target.setAttribute("data-x", currentPos.x);
                target.setAttribute("data-y", currentPos.y);
                let rotation = this.rotate();
                target.style.webkitTransform = target.style.transform =
                  "translate(" + currentPos.x + "px, " + currentPos.y + "px) " + rotation;

                // RAF-throttle guide overlay updates to at most one per frame.
                this._pendingGuides = guidesForThisFrame;
                if (!this._rafScheduled) {
                  this._rafScheduled = true;
                  requestAnimationFrame(() => {
                    this._rafScheduled = false;
                    if (this._snapSession && this._pendingGuides) {
                      setDragGuides(this._pendingGuides, this._snapSession.containerId);
                    }
                  });
                }

                // Move followers by the same delta (keep their own rotation)
                if (this._followers && this._followers.length > 0) {
                  this._followers.forEach(({ el }) => {
                    const fx = (parseFloat(el.getAttribute("data-x")) || 0) + event.dx;
                    const fy = (parseFloat(el.getAttribute("data-y")) || 0) + event.dy;
                    el.setAttribute("data-x", fx);
                    el.setAttribute("data-y", fy);
                    const fBase = (el.dataset && el.dataset.baseTransform) || "";
                    el.style.webkitTransform = el.style.transform =
                      "translate(" + fx + "px, " + fy + "px)" + (fBase ? " " + fBase : "");
                  });
                }

                // Else, drag the clone and update values in attributes in both elements
              } else {
                let target = document.getElementById("clone");
                let original = findBox(this.props.id);
                let x = (parseFloat(target.getAttribute("data-x"), 10) || 0) + event.dx;
                let y = (parseFloat(target.getAttribute("data-y"), 10) || 0) + event.dy;
                let translate = "translate(" + x + "px, " + y + "px)";
                target.style.WebkitTransform =
                  target.style.MozTransform =
                  target.style.msTransform =
                  target.style.OTransform =
                  target.style.transform =
                    translate + this.rotate();
                target.style.zIndex = "9999";

                target.setAttribute("data-x", x);
                target.setAttribute("data-y", y);
                original.setAttribute("data-x", x);
                original.setAttribute("data-y", y);
              }
            }
          },
          onend: (event) => {
            event.stopPropagation();
            let bar =
              this.props.containedViewSelected === 0
                ? document.getElementById("editorBoxIcons")
                : document.getElementById("contained_editorBoxIcons");
            if (bar) {
              bar.classList.remove("hidden");
            }
            setBoxPillsVisible(true);
            let target = event.target;
            target.style.opacity = 1;

            if (this.props.onDragEnd) {
              this.props.onDragEnd();
            }

            clearDragGuides();
            // Cache before nulling — release-snap below needs the targets.
            const snapSessionForRelease = this._snapSession;
            this._snapSession = null;
            this._pendingGuides = null;

            // Commit follower positions to Redux
            if (this._followers && this._followers.length > 0) {
              const container = event.target.parentNode;
              if (container) {
                this._followers.forEach(({ id: fid, el }) => {
                  const fx = parseFloat(el.getAttribute("data-x")) || 0;
                  const fy = parseFloat(el.getAttribute("data-y")) || 0;
                  const origLeft = parseFloat(el.style.left) || 0;
                  const origTop = parseFloat(el.style.top) || 0;
                  const finalLeft = origLeft + fx;
                  const finalTop = origTop + fy;

                  el.style.webkitTransform = el.style.transform =
                    (el.dataset && el.dataset.baseTransform) || "";
                  el.setAttribute("data-x", 0);
                  el.setAttribute("data-y", 0);
                  el.style.zIndex = restingZIndex(
                    fid,
                    this.props.boxSelected,
                    this.props.boxSelectedAll
                  );

                  const fBox = this.props.boxes[fid];
                  if (!fBox) return;
                  const absLeft = (finalLeft / container.offsetWidth) * 100 + "%";
                  const absTop = (finalTop / container.offsetHeight) * 100 + "%";
                  el.style.left = absLeft;
                  el.style.top = absTop;

                  this.props.onBoxMoved(
                    fid,
                    absLeft,
                    absTop,
                    fBox.position.type,
                    fBox.parent,
                    fBox.container,
                    { col: 0, row: 0 }
                  );
                });
              }
              this._followers = [];
            }

            // Commit the leader's own position; during multi-drag boxSelected is -1.
            const isMultiDragLeader =
              Array.isArray(this.props.boxSelectedAll) &&
              this.props.boxSelectedAll.includes(this.props.id);
            if (this.props.boxSelected !== this.props.id && !isMultiDragLeader) {
              return;
            }

            if (!target.parentNode) {
              return;
            }
            // Unhide EditorShortcuts

            // Get position and if contained in sortableContainer || PluginPlaceHolder, convert to %
            let pos = this.props.boxes[this.props.id].position.type;
            let left, top, absoluteLeft, absoluteTop;

            if (isSortableContainer(box.container)) {
              // For sortable containers, use the data attributes and convert to percentages
              let actualLeft = target.getAttribute("data-x") || target.style.left;
              let actualTop = target.getAttribute("data-y") || target.style.top;
              left =
                Math.max(
                  Math.min((parseFloat(actualLeft) / target.parentElement.offsetWidth) * 100, 100),
                  0
                ) + "%";
              top =
                Math.max(
                  Math.min((parseFloat(actualTop) / target.parentElement.offsetHeight) * 100, 100),
                  0
                ) + "%";
              target.style.left = left;
              target.style.top = top;
            } else {
              // For non-sortable containers, convert transform to left/top
              let x = parseFloat(target.getAttribute("data-x")) || 0;
              let y = parseFloat(target.getAttribute("data-y")) || 0;

              // Get the original position before dragging (handle % values)
              let originalLeft, originalTop;
              if (target.style.left && target.style.left.includes("%")) {
                originalLeft =
                  target.parentElement.offsetWidth * (parseFloat(target.style.left) / 100);
              } else {
                originalLeft = parseFloat(target.style.left) || 0;
              }

              if (target.style.top && target.style.top.includes("%")) {
                originalTop =
                  target.parentElement.offsetHeight * (parseFloat(target.style.top) / 100);
              } else {
                originalTop = parseFloat(target.style.top) || 0;
              }

              // Calculate final position
              let finalLeft = originalLeft + x;
              let finalTop = originalTop + y;

              // Smart snap on release — priority order in snapOnRelease.
              if (snapSessionForRelease) {
                const rect = target.getBoundingClientRect();
                const snapped = snapOnRelease({
                  draggedRect: {
                    left: finalLeft,
                    top: finalTop,
                    width: rect.width,
                    height: rect.height,
                  },
                  snapData: snapSessionForRelease,
                  gridEnabled: !!this.props.grid,
                });
                finalLeft = snapped.left;
                finalTop = snapped.top;
              }

              // Keep question boxes on-canvas (pin top to 0 if taller than the slide).
              if (config.category === "evaluation") {
                const parentW = target.parentElement.offsetWidth;
                const parentH = target.parentElement.offsetHeight;
                const boxW = target.offsetWidth;
                const boxH = target.offsetHeight;
                finalLeft = Math.min(Math.max(finalLeft, 0), Math.max(0, parentW - boxW));
                finalTop = Math.min(Math.max(finalTop, 0), Math.max(0, parentH - boxH));
              }

              // Reset transform (but preserve rotation) and apply final position
              target.style.webkitTransform = target.style.transform = this.rotate();
              target.style.left = finalLeft + "px";
              target.style.top = finalTop + "px";

              // Reset data attributes
              target.setAttribute("data-x", 0);
              target.setAttribute("data-y", 0);

              // Calculate percentage values for the store
              absoluteLeft = (finalLeft / target.parentElement.offsetWidth) * 100 + "%";
              absoluteTop = (finalTop / target.parentElement.offsetHeight) * 100 + "%";

              // Apply percentage values if position type is absolute
              if (pos === "absolute") {
                target.style.left = absoluteLeft;
                target.style.top = absoluteTop;
              }
              // For relative positioning, keep the pixel values as they are
            }

            target.style.zIndex = restingZIndex(
              this.props.id,
              this.props.boxSelected,
              this.props.boxSelectedAll
            );

            // Delete clone and unhide original

            let clone = document.getElementById("clone");
            if (clone) {
              clone.parentElement.removeChild(clone);
            }
            if (isSortableContainer(box.container)) {
              target.style.opacity = 1;
            }

            let releaseClickEl = document.elementFromPoint(event.clientX, event.clientY);
            let row = releaseClick(releaseClickEl, "rowNum");
            let col = releaseClick(releaseClickEl, "colNum");
            let hoverSortableContainer;
            let calculatedId = releaseClick(releaseClickEl, ID_PREFIX_SORTABLE_CONTAINER);
            if (calculatedId) {
              hoverSortableContainer = ID_PREFIX_SORTABLE_CONTAINER + calculatedId;
            }
            let containerId = hoverSortableContainer || box.container;
            let disposition = { col: col || 0, row: row || 0 };
            let containerHoverID = releaseClick(releaseClickEl, "sc-");

            // Always update the position after dragging
            // Get the final position values to save
            let finalLeft = isSortableContainer(box.container)
              ? left
              : pos === "absolute"
                ? absoluteLeft
                : target.style.left;
            let finalTop = isSortableContainer(box.container)
              ? top
              : pos === "absolute"
                ? absoluteTop
                : target.style.top;

            this.props.onBoxMoved(
              this.props.id,
              finalLeft,
              finalTop,
              this.props.boxes[this.props.id].position.type,
              box.parent,
              containerHoverID ? "sc-" + containerHoverID : containerId,
              disposition
            );

            event.stopPropagation();
          },
        })
        .resizable({
          snap: { targets: targets },
          snapSize: {
            targets: [
              // snap the width and height to multiples of 5 when the element size
              // is 25 pixels away from the target size
              { width: SNAP_SIZE, height: SNAP_SIZE, range: SNAP_SIZE },
            ],
          },
          modifiers: this.getResizeModifiers(),
          enabled: !config.notResizable && !this.isNestedInAssessment() && !box.timeline?.locked,
          restrict: {
            restriction: resizeRestrictionSelector,
          },
          // Stop text boxes from being shrunk smaller than their content, so
          // the text never spills outside the box (smart elements already stay
          // content-sized via their auto height).
          restrictSize: {
            min: () => this.getResizeMin(),
          },
          margin: 10,
          allowFrom: ".helpersResizable",
          edges: { left: true, right: true, bottom: true, top: true },
          onstart: (event) => {
            // Hide EditorShortcuts
            if (this.props.boxSelected !== this.props.id) {
              return;
            }
            let bar =
              this.props.containedViewSelected === 0
                ? document.getElementById("editorBoxIcons")
                : document.getElementById("contained_editorBoxIcons");
            if (bar) {
              bar.classList.add("hidden");
            }
            setBoxPillsVisible(false);
            // Append textbox with actual size
            let sb = document.getElementsByClassName("selectedBox");
            if (
              sb &&
              sb[0] &&
              "box-" + this.props.boxSelected === sb[0].getAttribute("id") &&
              !document.getElementById("sizing")
            ) {
              let span = document.createElement("span");
              span.setAttribute("id", "sizing");
              sb[0].appendChild(span);
            }

            // Separate snap session for resize (independent from drag).
            const containerId =
              this.props.containedViewSelected === 0 ? "maincontent" : "contained_maincontent";
            this._resizeSnapSession = {
              containerId,
              ...buildSnapTargets({
                boxes: this.props.boxes,
                draggedBoxId: this.props.id,
                containerId,
                pixelGrid: !!this.props.grid,
              }),
            };
            // Cache start rect in container-local coords so onend can tell
            // which edges actually moved (only those get snapped).
            if (this._resizeSnapSession.containerRect) {
              const startR = event.target.getBoundingClientRect();
              const cr = this._resizeSnapSession.containerRect;
              this._resizeStartRect = {
                left: startR.left - cr.left,
                top: startR.top - cr.top,
                right: startR.right - cr.left,
                bottom: startR.bottom - cr.top,
              };
            }
            this._resizeRafScheduled = false;

            // --- Rotated-resize session --------------------------------------
            // interact.js works entirely in screen/axis-aligned space, so its
            // event.rect and edge-detection are wrong once the box is rotated
            // (dragging a local corner near ±45° looks to interact like a single
            // AABB edge, coupling width into height). When rotated we drive the
            // resize ourselves from the pointer delta projected onto the box's
            // own local axes, keeping the opposite corner pinned.
            // ponytail: local axes ignore scaleX/scaleY mirror flips; a box that
            // is both rotated AND flipped would anchor to the wrong corner. Add
            // flip-aware axis signs here if that combination ever ships.
            this._rotResize = null;
            const rotDeg = (this.props.pluginToolbars[this.props.id].structure.rotation || 0) % 360;
            const rotated = Math.abs(rotDeg) > 0.01 && Math.abs(rotDeg) < 359.99;
            if (rotated) {
              // Which corner handle was grabbed. The handle records itself on
              // pointerdown (this._grabbedCorner) — the reliable source. Fall back
              // to the pointerdown target's data-corner, then to interact's edges
              // (unreliable once rotated: on a north grab the AABB shows only the
              // top edge, which mis-maps to "ne" and inverts the box).
              const downTarget =
                event.interaction && event.interaction.downEvent
                  ? event.interaction.downEvent.target
                  : null;
              let corner =
                this._grabbedCorner ||
                (downTarget && downTarget.getAttribute
                  ? downTarget.getAttribute("data-corner")
                  : null);
              if (!corner) {
                const e = event.edges || {};
                const v = e.top ? "n" : e.bottom ? "s" : "";
                const h = e.left ? "w" : e.right ? "e" : "";
                corner = v + h || "se";
              }
              const scale = this.getCanvasScale();
              const rad = (rotDeg * Math.PI) / 180;
              const cos = Math.cos(rad);
              const sin = Math.sin(rad);
              const t = event.target;
              const rect = t.getBoundingClientRect();
              const center = { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
              // offsetWidth/Height are the unrotated local (unscaled) box size.
              const startW = t.offsetWidth;
              const startH = t.offsetHeight;
              // Screen-space unit vectors of the box's local X/Y axes.
              const ux = { x: cos, y: sin };
              const uy = { x: -sin, y: cos };
              // Dragged-corner signs (local X right = +, local Y down = +).
              // 0 = axis not driven by this handle (single-edge grips like "s").
              const cx = corner.indexOf("e") !== -1 ? 1 : corner.indexOf("w") !== -1 ? -1 : 0;
              const cy = corner.indexOf("s") !== -1 ? 1 : corner.indexOf("n") !== -1 ? -1 : 0;
              // Anchor = opposite corner, pinned in screen space for the gesture.
              const ax = -cx;
              const ay = -cy;
              const anchor = {
                x:
                  center.x +
                  ax * ((startW / 2) * scale) * ux.x +
                  ay * ((startH / 2) * scale) * uy.x,
                y:
                  center.y +
                  ax * ((startW / 2) * scale) * ux.y +
                  ay * ((startH / 2) * scale) * uy.y,
              };
              this._rotResize = {
                corner,
                rotationDeg: rotDeg,
                ax,
                ay,
                ux,
                uy,
                scale,
                startW,
                startH,
                startX: event.clientX,
                startY: event.clientY,
                anchor,
                aspect: this.isAspectLockActive(),
              };
            }

            event.stopPropagation();
          },
          onmove: (event) => {
            event.stopPropagation();
            if (this.props.boxSelected !== this.props.id) {
              return;
            }
            let target = event.target;

            // Rotated resize: drive from pointer delta in the box's local frame,
            // keeping the opposite corner pinned. Bypasses interact's screen-space
            // rect entirely (see the _rotResize session built in onstart).
            if (this._rotResize) {
              const rr = this._rotResize;
              const minSize = this.getResizeMin();
              const { newW, newH } = computeRotatedResize({
                rotationDeg: rr.rotationDeg,
                corner: rr.corner,
                dxScreen: event.clientX - rr.startX,
                dyScreen: event.clientY - rr.startY,
                startW: rr.startW,
                startH: rr.startH,
                scale: rr.scale,
                aspect: rr.aspect,
                minW: minSize.width / rr.scale,
                minH: minSize.height / rr.scale,
              });
              target.style.width = newW + "px";
              target.style.height = newH + "px";
              // Reposition so the pinned anchor corner stays put. Apply a tentative
              // transform, measure, then correct the translate.
              let tx = parseFloat(target.getAttribute("data-x")) || 0;
              let ty = parseFloat(target.getAttribute("data-y")) || 0;
              const applyTransform = () => {
                const tr = "translate(" + tx + "px," + ty + "px)" + this.rotate();
                target.style.WebkitTransform =
                  target.style.MozTransform =
                  target.style.msTransform =
                  target.style.OTransform =
                  target.style.transform =
                    tr;
              };
              applyTransform();
              const rect2 = target.getBoundingClientRect();
              const center2 = {
                x: (rect2.left + rect2.right) / 2,
                y: (rect2.top + rect2.bottom) / 2,
              };
              // Where the anchor corner currently sits with the new size.
              const curAnchor = {
                x:
                  center2.x +
                  rr.ax * ((newW / 2) * rr.scale) * rr.ux.x +
                  rr.ay * ((newH / 2) * rr.scale) * rr.uy.x,
                y:
                  center2.y +
                  rr.ax * ((newW / 2) * rr.scale) * rr.ux.y +
                  rr.ay * ((newH / 2) * rr.scale) * rr.uy.y,
              };
              tx += (rr.anchor.x - curAnchor.x) / rr.scale;
              ty += (rr.anchor.y - curAnchor.y) / rr.scale;
              applyTransform();
              target.setAttribute("data-x", tx);
              target.setAttribute("data-y", ty);
              // Live size readout.
              const span = document.getElementById("sizing");
              if (span) {
                span.innerHTML =
                  parseInt(target.style.width, 10) + " × " + parseInt(target.style.height, 10);
              }
              return;
            }

            let x = parseFloat(target.getAttribute("data-x"), 10) || 0;
            let y = parseFloat(target.getAttribute("data-y"), 10) || 0;
            // interact's rect/deltaRect are on-screen px; divide by the canvas
            // zoom so the box's (unscaled) local dimensions track the cursor.
            const scale = this.getCanvasScale();
            // update the element's style
            let w = event.rect.width / scale;
            let h = event.rect.height / scale;
            toolbar = this.props.pluginToolbars[this.props.id];
            let cos = Math.cos(((toolbar.structure.rotation || 0) * Math.PI) / 180);
            let sin = Math.sin(((toolbar.structure.rotation || 0) * Math.PI) / 180);
            let r = cos * cos - sin * sin; // = cos(2θ)
            // target.style.width = (h*sin-w*cos) + 'px';
            // target.style.height = (h*cos-w*sin) + 'px';

            // r goes to 0 near ±45°/±135° (division blows up) and the raw
            // formula yields NEGATIVE dimensions at 180°/270° — which collapsed
            // the box and made it un-resizable once rotated. Guard the
            // singularity and force positive dimensions.
            let newW = (w * cos - h * sin) / r;
            let newH = (h * cos - w * sin) / r;
            if (!isFinite(newW) || !isFinite(newH) || Math.abs(r) < 0.0001) {
              newW = w;
              newH = h;
            }
            target.style.width = Math.abs(newW) + "px";
            target.style.height = Math.abs(newH) + "px";

            // toolbar =   this.props.pluginToolbars[this.props.id];
            //
            // let cos = Math.cos(toolbar.structure.rotation*Math.PI /180);
            // let sin = Math.sin(toolbar.structure.rotation*Math.PI /180);
            // console.log(event.rect.width, event.rect.height)
            // // target.style.width = (event.rect.height * cos + event.rect.width* sin  ) + "px";
            // // target.style.height =  (event.rect.height * sin + event.rect.width* cos  ) + "px";
            // target.style.width = (event.rect.width * sin) + "px";
            // target.style.height = (event.rect.width * cos) + "px";
            // translate when resizing from top or left edges (screen px → local)
            x += event.deltaRect.left / scale;
            y += event.deltaRect.top / scale;
            if (box.resizable) {
              // Only in slide
              let translate = "translate(" + x + "px," + y + "px)";
              target.style.WebkitTransform =
                target.style.MozTransform =
                target.style.msTransform =
                target.style.OTransform =
                target.style.transform =
                  translate + this.rotate();
              target.setAttribute("data-x", x);
              target.setAttribute("data-y", y);
            }
            // Update size in textbox
            let span = document.getElementById("sizing");
            if (span) {
              span.innerHTML =
                parseInt(target.style.width, 10) + " × " + parseInt(target.style.height, 10);
            }

            // Guide resolution measures the DOM (getBoundingClientRect) right
            // after the style writes above — a forced reflow. Do it at most
            // once per frame instead of on every pointermove, otherwise
            // resizing feels laggy on high-Hz pointing devices.
            if (this._resizeSnapSession && this._resizeSnapSession.containerRect) {
              if (!this._resizeRafScheduled) {
                this._resizeRafScheduled = true;
                requestAnimationFrame(() => {
                  this._resizeRafScheduled = false;
                  const session = this._resizeSnapSession;
                  if (!session || !session.containerRect) {
                    return;
                  }
                  const containerRect = session.containerRect;
                  const newRect = target.getBoundingClientRect();
                  const draggedRect = {
                    left: newRect.left - containerRect.left,
                    top: newRect.top - containerRect.top,
                    width: newRect.width,
                    height: newRect.height,
                  };
                  const { activeGuides } = resolveSnap({ draggedRect, snapData: session });
                  setDragGuides(activeGuides, session.containerId);
                });
              }
            }
          },
          onend: (event) => {
            if (this.props.boxSelected !== this.props.id) {
              return;
            }
            clearDragGuides();
            // Cache before nulling — release-snap below needs the targets.
            const resizeSnapForRelease = this._resizeSnapSession;
            const startRect = this._resizeStartRect;
            this._resizeSnapSession = null;
            this._resizeStartRect = null;
            this._rotResize = null;
            this._grabbedCorner = null;
            let target = event.target;

            // getBoundingClientRect below returns the AXIS-ALIGNED bounding box.
            // For a rotated box that box is larger than the element's own local
            // size, so the snap branch would overwrite the rotation-corrected
            // width/height computed in onmove with the AABB size and make the box
            // jump on release. Skip AABB-based snapping while rotated; onmove's
            // local size stands. (Precise edge-snapping for rotated boxes is a
            // separate, bigger feature.)
            const resizeRotation =
              (this.props.pluginToolbars[this.props.id].structure.rotation || 0) % 360;
            const isRotated = Math.abs(resizeRotation) > 0.01 && Math.abs(resizeRotation) < 359.99;

            // Snap final rect: only edges that moved get snapped. Updates
            // target.style.width/height and data-x/y; the unit conversion
            // below picks these up.
            if (
              !isRotated &&
              resizeSnapForRelease &&
              resizeSnapForRelease.containerRect &&
              startRect
            ) {
              const cr = resizeSnapForRelease.containerRect;
              const endR = target.getBoundingClientRect();
              const localEnd = {
                left: endR.left - cr.left,
                top: endR.top - cr.top,
                width: endR.width,
                height: endR.height,
              };
              const movableEdges = {
                left: Math.abs(localEnd.left - startRect.left) > 1,
                right: Math.abs(localEnd.left + localEnd.width - startRect.right) > 1,
                top: Math.abs(localEnd.top - startRect.top) > 1,
                bottom: Math.abs(localEnd.top + localEnd.height - startRect.bottom) > 1,
              };
              const snapped = snapResizeOnRelease({
                rect: localEnd,
                snapData: resizeSnapForRelease,
                gridEnabled: !!this.props.grid,
                movableEdges,
              });
              // localEnd/snapped are on-screen px (getBoundingClientRect);
              // style.width and data-x are unscaled local px → divide by zoom.
              const scale = this.getCanvasScale();
              const dxLeft = (snapped.left - localEnd.left) / scale;
              const dyTop = (snapped.top - localEnd.top) / scale;
              target.style.width = snapped.width / scale + "px";
              target.style.height = snapped.height / scale + "px";
              const curX = parseFloat(target.getAttribute("data-x")) || 0;
              const curY = parseFloat(target.getAttribute("data-y")) || 0;
              target.setAttribute("data-x", curX + dxLeft);
              target.setAttribute("data-y", curY + dyTop);
            }

            let structure = this.props.pluginToolbars[this.props.id].structure;
            let widthButton = Object.assign(
              {},
              { value: structure.width, units: structure.widthUnit }
            );
            let heightButton = Object.assign(
              {},
              { value: structure.height, units: structure.heightUnit }
            );

            // Units can be either % or px. Always recompute from the dragged
            // DOM size — even when the box's stored value is still "auto" —
            // otherwise a box that started as auto (BasicText/RichText's
            // default) can never be resized: onmove visually resizes the
            // element, but this used to skip committing a concrete value
            // whenever the PREVIOUS value was "auto", so onBoxResized kept
            // dispatching "auto" and the box snapped back to its content-fit
            // size the instant the drag ended.
            if (widthButton.units === "%") {
              let newWidth = Math.min(
                (parseFloat(target.style.width) / target.parentElement.offsetWidth) * 100,
                100
              );
              widthButton.value = newWidth;
            } else {
              widthButton.value = parseFloat(target.style.width);
            }

            if (heightButton.units === "%") {
              let newHeight = Math.min(
                (parseFloat(target.style.height) / target.parentElement.offsetHeight) * 100,
                100
              );
              heightButton.value = newHeight;
            } else {
              heightButton.value = parseFloat(target.style.height);
            }

            // Use max-content (not auto) so the box keeps its natural width
            // when positioned past the parent's right edge — matches render style.
            target.style.width =
              widthButton.value === "auto" ? "max-content" : widthButton.value + widthButton.units;
            target.style.height =
              heightButton.value === "auto" ? "auto" : heightButton.value + heightButton.units;

            this.props.onBoxResized(this.props.id, {
              width: widthButton.value,
              widthUnit: widthButton.units,
              height: heightButton.value,
              heightUnit: heightButton.units,
              x: box.resizable
                ? (((parseFloat(target.style.left) / 100) * target.parentElement.offsetWidth +
                    parseFloat(target.getAttribute("data-x"))) *
                    100) /
                    target.parentElement.offsetWidth +
                  "%"
                : 0,
              y: box.resizable
                ? (((parseFloat(target.style.top) / 100) * target.parentElement.offsetHeight +
                    parseFloat(target.getAttribute("data-y"))) *
                    100) /
                    target.parentElement.offsetHeight +
                  "%"
                : 0,
            });
            let translate = "translate(0px, 0px)";
            target.style.WebkitTransform =
              target.style.MozTransform =
              target.style.msTransform =
              target.style.OTransform =
              target.style.transform =
                translate + this.rotate();
            target.setAttribute("data-x", 0);
            target.setAttribute("data-y", 0);

            // Unhide EditorShorcuts and remove size textbox
            let bar =
              this.props.containedViewSelected === 0
                ? document.getElementById("editorBoxIcons")
                : document.getElementById("contained_editorBoxIcons");
            if (bar) {
              bar.classList.remove("hidden");
            }
            setBoxPillsVisible(true);
            let span = document.getElementById("sizing");
            if (span) {
              span.parentElement.removeChild(span);
            }
            event.stopPropagation();
            // this.forceUpdate();
          },
        });
    }
  }

  /**
   * Calculate element position from left
   * @param left element's left
   * @param width element's width
   * @returns {position} Position from left
   */
  getElementPositionFromLeft(left, width) {
    if (left.indexOf("px") !== -1) {
      return left;
    } else if (left.indexOf("%") !== -1) {
      return (width * parseFloat(left)) / 100;
    }
    return 0;
  }

  /**
   * Before component unmounts
   * Unset interact listeners
   */
  componentWillUnmount() {
    if (this._onShiftKey) {
      window.removeEventListener("keydown", this._onShiftKey);
      window.removeEventListener("keyup", this._onShiftKey);
      window.removeEventListener("blur", this._onShiftKey);
    }
    if (this.nodeRef.current) {
      interact(this.nodeRef.current).unset();
    }
    // Clear stale guides if unmounted mid-interaction (e.g. scene switch).
    if (this._snapSession || this._resizeSnapSession) {
      clearDragGuides();
      this._snapSession = null;
      this._resizeSnapSession = null;
      this._resizeStartRect = null;
      this._rotResize = null;
    }
  }

  rotate() {
    let rotate = "rotate(0deg) ";
    let toolbar = this.props.pluginToolbars[this.props.id];
    if (!(this.props.markCreatorId && this.props.id === this.props.boxSelected)) {
      if (toolbar.structure.rotation && toolbar.structure.rotation) {
        rotate = " rotate(" + toolbar.structure.rotation + "deg) ";
      }

      if (toolbar.structure.horizontalFlip) {
        rotate += " scaleX(-1)";
      }

      if (toolbar.structure.verticalFlip) {
        rotate += " scaleY(-1)";
      }
    }

    return rotate;
  }
}

EditorBox.propTypes = {
  /**
   * Box unique identifier
   */
  id: PropTypes.string.isRequired,
  /**
   * Object containing all created boxes (by id)
   */
  boxes: PropTypes.object.isRequired,
  /**
   * Box selected. If there is none selected the value is, -1
   */
  boxSelected: PropTypes.any.isRequired,
  /**
   * Depth level of the selected box. Used when there are plugins inside plugins
   */
  boxLevelSelected: PropTypes.number.isRequired,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object.isRequired,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any.isRequired,
  /**
   * Object containing all the toolbars
   */
  pluginToolbars: PropTypes.object.isRequired,
  /**
   * Last action dispatched in Redux
   */
  lastActionDispatched: PropTypes.any.isRequired,
  /**
   * Callback for when adding a mark
   */
  addMarkShortcut: PropTypes.func.isRequired,
  /**
   * Callback for when deleting a mark
   */
  deleteMarkCreator: PropTypes.func.isRequired,
  /**
   * Identifier of the box that is currently in process of creating a mark
   */
  markCreatorId: PropTypes.any.isRequired,
  /**
   * Object containing box marks
   */
  marks: PropTypes.object,
  /**
   * Callback for adding a box
   */
  onBoxAdded: PropTypes.func.isRequired,
  /**
   * Selects a box
   */
  onBoxSelected: PropTypes.func.isRequired,
  /**
   * Icreases box level selected
   */
  onBoxLevelIncreased: PropTypes.func.isRequired,
  /**
   * Callback for when moving a box
   */
  onBoxMoved: PropTypes.func.isRequired,
  /**
   * Callback for when resizing a box
   */
  onBoxResized: PropTypes.func.isRequired,
  /**
   * Callback for when dropping a box
   */
  onBoxDropped: PropTypes.func.isRequired,
  /**
   * Callback for when vertically aligning boxes inside a container
   */
  onVerticallyAlignBox: PropTypes.func.isRequired,
  /**
   * Callback for when reordering boxes inside a container
   */
  onBoxesInsideSortableReorder: PropTypes.func.isRequired,
  /**
   * Callback for when resizing a sortable container
   */
  onSortableContainerResized: PropTypes.func.isRequired,
  /**
   * Callback for toggling the CKEDitor
   */
  onTextEditorToggled: PropTypes.func.isRequired,
  /**
   * Page type the box is at
   */
  pageType: PropTypes.string.isRequired,
  /**
   * Callback for toggling the Rich Marks Modal
   */
  onRichMarksModalToggled: PropTypes.func.isRequired,
  /**
   * Snap to grid flag
   */
  grid: PropTypes.bool,
  /**
   * Object containing all exercises
   */
  exercises: PropTypes.object,
  /**
   * Function for setting the right answer of an exercise
   */
  setCorrectAnswer: PropTypes.func.isRequired,
  /**
   * Current page
   */
  page: PropTypes.any,
  /**
   * Function that updates the toolbar of a view
   */
  onToolbarUpdated: PropTypes.func,
  /**
   * Callback for when dragging starts
   */
  onDragStart: PropTypes.func,
  /**
   * Callback for when dragging ends
   */
  onDragEnd: PropTypes.func,
};
